'use client'

// The job centre's own look (Sam, 30 Oct: "themed like Job center too - not the same UI as Pino's interface", and then
// "can you use the job centre plus brand colours for the overlay"): an office on a high street in Jobcentre Plus's lime
// green, yellow and white, the jobcentreplus wordmark over the door as on the building's banner, painted walls, a cork
// board with typed index cards pinned to it, a red rubber stamp, forms in boxes, square buttons. None of the town's
// rounded cream kit. White on that lime is hard to read small, so buttons and small type are a deeper green of the same
// family, and the lime and yellow carry the sign, the tabs and the number board.

export const look = {
  lime: '#84b817',
  limeDeep: '#5f8a0d',
  yellow: '#ffec00',
  // (buttons, links and small type: white on it reads at any size)
  sign: '#3b6309',
  signDeep: '#5f8a0d',
  wall: '#f3f7ec',
  wallDeep: '#e2ebd0',
  cork: '#b9895a',
  card: '#fffdf5',
  rule: 'rgba(70, 110, 170, 0.18)',
  redLine: 'rgba(200, 55, 45, 0.55)',
  ink: '#22282a',
  inkSoft: '#5d6663',
  pin: '#c8372d',
  stamp: '#b3261e',
  led: '#ffec00',
  sans: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
  type: '"Courier New", Courier, ui-monospace, monospace',
}

// the condensed bold the wordmark is set in (the building's banner is Arial Narrow Bold too)
const NARROW = '"Arial Narrow", "Helvetica Neue Condensed", "Roboto Condensed", Arial, sans-serif'

/** jobcentreplus, as on the banner over the door: job and plus white, centre yellow. */
export function Wordmark({ size = 28 }) {
  return (
    <span aria-label="Jobcentre Plus" style={{ fontFamily: NARROW, fontWeight: 700, fontSize: size, letterSpacing: '-0.02em', lineHeight: 1 }}>
      <span style={{ color: '#fff' }}>job</span><span style={{ color: look.yellow }}>centre</span><span style={{ color: '#fff' }}>plus</span>
    </span>
  )
}

/** The sign over the door: the wordmark on lime, and a darker band under it for the small print. */
export function Sign({ sub }) {
  return (
    <div>
      <div style={{ background: look.lime, padding: '14px 16px 12px' }}><Wordmark size={30} /></div>
      {sub && <div style={{ background: look.limeDeep, color: '#fff', padding: '5px 16px', fontFamily: look.sans, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{sub}</div>}
    </div>
  )
}

/** A square office button; with `href` it is a link dressed as one. */
export function Btn({ children, onClick, primary = false, disabled = false, full = false, small = false, type = 'button', href = null, target }) {
  const style = {
    display: full ? 'block' : 'inline-block', width: full ? '100%' : undefined, boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none',
    cursor: disabled ? 'default' : 'pointer', padding: small ? '6px 10px' : '10px 14px', borderRadius: 2, border: `2px solid ${look.sign}`,
    background: primary ? look.sign : '#fff', color: primary ? '#fff' : look.sign, opacity: disabled ? 0.5 : 1,
    fontFamily: look.sans, fontWeight: 800, fontSize: small ? 11 : 13, letterSpacing: '0.06em', textTransform: 'uppercase',
  }
  if (href) return <a href={href} target={target} rel={target ? 'noopener' : undefined} style={style}>{children}</a>
  return <button type={type} onClick={onClick} disabled={disabled} style={style}>{children}</button>
}

/** A typed index card pinned to the board, a little askew. */
export function IndexCard({ children, tilt = 0, faded = false }) {
  return (
    <div style={{
      position: 'relative', background: look.card, padding: '18px 14px 12px', transform: `rotate(${tilt}deg)`,
      boxShadow: '0 2px 0 rgba(0,0,0,0.18), 0 6px 14px rgba(0,0,0,0.18)', opacity: faded ? 0.75 : 1,
      backgroundImage: `linear-gradient(${look.redLine}, ${look.redLine}), repeating-linear-gradient(transparent 0 21px, ${look.rule} 21px 22px)`,
      backgroundSize: '100% 1.5px, 100% 100%', backgroundPosition: '0 30px, 0 8px', backgroundRepeat: 'no-repeat, repeat',
      fontFamily: look.type, color: look.ink,
    }}>
      <span style={{ position: 'absolute', top: 6, left: '50%', width: 12, height: 12, marginLeft: -6, borderRadius: '50%', background: look.pin, boxShadow: 'inset -2px -2px 0 rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.4)' }} />
      {children}
    </div>
  )
}

/** The rubber stamp across a card. */
export function Stamp({ children }) {
  return (
    <div style={{
      position: 'absolute', right: 10, top: 28, transform: 'rotate(-12deg)', border: `3px solid ${look.stamp}`, color: look.stamp,
      padding: '2px 8px', fontFamily: look.sans, fontWeight: 900, fontSize: 18, letterSpacing: '0.18em', opacity: 0.82, pointerEvents: 'none',
    }}>{children}</div>
  )
}

/** The cork board everything is pinned to. */
export function Board({ children }) {
  return (
    <div style={{
      background: look.cork, padding: 14, display: 'grid', gap: 16,
      backgroundImage: 'radial-gradient(rgba(0,0,0,0.12) 1px, transparent 1.4px), radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1.4px)',
      backgroundSize: '7px 7px, 11px 11px', boxShadow: 'inset 0 0 0 6px #8c6236, inset 0 0 0 8px #6f4b27',
    }}>{children}</div>
  )
}

/** A label on a form, in capitals. */
export function Label({ children }) {
  return <div style={{ fontFamily: look.sans, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: look.sign, margin: '8px 0 3px' }}>{children}</div>
}

export const field = {
  width: '100%', boxSizing: 'border-box', padding: '9px 10px', border: `1.5px solid ${look.inkSoft}`, borderRadius: 0, background: '#fff',
  fontFamily: look.type, fontSize: 15, color: look.ink, outline: 'none', letterSpacing: '0.04em',
}

/** Small print. */
export function Small({ children, style = {} }) {
  return <div style={{ fontFamily: look.sans, fontSize: 12.5, lineHeight: 1.45, color: look.inkSoft, ...style }}>{children}</div>
}

/** The red number board over the counter. */
export function Led({ label, value }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1c2a0c', padding: '5px 9px', borderRadius: 2, border: `2px solid ${look.limeDeep}` }}>
      <span style={{ fontFamily: look.sans, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em', color: '#c6d6a8', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontFamily: look.type, fontWeight: 900, fontSize: 17, color: look.led, textShadow: `0 0 6px ${look.led}`, letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  )
}
